/**
 * Task Dependency Validation Service - Phase 4 Architecture
 *
 * Encapsulates dependency validation and circular dependency detection.
 * Extracted from TaskDependencyService to reduce single-responsibility violations.
 */

import { typedSupabase } from "@/lib/typed-supabase";
import { logger } from "@/lib/logger";
import { IService, ServiceResponse } from "@/services/core/service-registry";
import type {
  CircularDependency,
  CreateTaskDependencyRequest,
  TaskDependency,
  ValidationResult,
} from "@/types/time-management";

export class TaskDependencyValidationService implements IService {
  public readonly name = "TaskDependencyValidationService";
  private readonly supabase = typedSupabase;

  async initialize(): Promise<void> {
    logger.info("✅ TaskDependencyValidationService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("✅ TaskDependencyValidationService disposed");
  }

  async validateDependency(
    dependency: TaskDependency | CreateTaskDependencyRequest
  ): Promise<ServiceResponse<ValidationResult>> {
    try {
      const issues: string[] = [];
      const warnings: string[] = [];

      if (dependency.predecessor_task_id === dependency.successor_task_id) {
        issues.push("Task cannot depend on itself");
      }

      const circularResult = await this.checkCircularDependency(
        dependency.predecessor_task_id,
        dependency.successor_task_id
      );

      if (!circularResult.success) {
        return { success: false, error: circularResult.error };
      }

      if (circularResult.data) {
        issues.push("This dependency would create a circular dependency");
      }

      const existingDependency = await this.supabase
        .from("task_dependencies")
        .select("id")
        .eq("predecessor_task_id", dependency.predecessor_task_id)
        .eq("successor_task_id", dependency.successor_task_id)
        .single();

      if (existingDependency.data) {
        issues.push("This dependency already exists");
      }

      if (dependency.lag_time && dependency.lag_time < 0) {
        warnings.push("Negative lag time may cause scheduling issues");
      }

      const isValid = issues.length === 0;

      return {
        success: true,
        data: {
          isValid,
          errors: issues,
          warnings,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async detectCircularDependencies(
    taskId: string
  ): Promise<ServiceResponse<CircularDependency[]>> {
    try {
      const circularDependencies: CircularDependency[] = [];
      const visited = new Set<string>();
      const path: string[] = [];

      const detectCircular = async (currentTaskId: string): Promise<void> => {
        if (path.includes(currentTaskId)) {
          const cycleStartIndex = path.indexOf(currentTaskId);
          const cycle = path.slice(cycleStartIndex);
          cycle.push(currentTaskId);

          circularDependencies.push({
            task_ids: cycle,
            dependency_chain: [],
          });
          return;
        }

        if (visited.has(currentTaskId)) {
          return;
        }

        visited.add(currentTaskId);
        path.push(currentTaskId);

        const { data: successors } = await this.supabase
          .from("task_dependencies")
          .select("successor_task_id")
          .eq("predecessor_task_id", currentTaskId);

        if (successors) {
          for (const successor of successors) {
            await detectCircular(successor.successor_task_id);
          }
        }

        path.pop();
      };

      await detectCircular(taskId);

      return { success: true, data: circularDependencies };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async checkCircularDependency(
    predecessorId: string,
    successorId: string
  ): Promise<ServiceResponse<boolean>> {
    try {
      const { data: existingDeps, error: depsError } = await this.supabase
        .from("task_dependencies")
        .select("*")
        .or(
          `predecessor_task_id.eq.${predecessorId},successor_task_id.eq.${successorId}`
        );

      if (depsError) {
        return await this.manualCircularDependencyCheck(
          predecessorId,
          successorId
        );
      }

      const wouldCreateCycle =
        existingDeps?.some(
          (dep) =>
            dep.predecessor_task_id === successorId &&
            dep.successor_task_id === predecessorId
        ) || false;

      return { success: true, data: wouldCreateCycle };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async bulkValidateDependencies(
    taskIds: string[]
  ): Promise<ServiceResponse<ValidationResult[]>> {
    try {
      const results: ValidationResult[] = [];

      for (const taskId of taskIds) {
        const circularResult = await this.detectCircularDependencies(taskId);
        if (!circularResult.success) {
          results.push({
            isValid: false,
            errors: [circularResult.error],
            warnings: [],
          });
        } else {
          results.push({
            isValid: circularResult.data.length === 0,
            errors: circularResult.data.map(
              (cd) =>
                `Circular dependency detected in tasks: ${cd.task_ids.join(" → ")}`
            ),
            warnings: [],
          });
        }
      }

      return { success: true, data: results };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  private async manualCircularDependencyCheck(
    predecessorId: string,
    successorId: string
  ): Promise<ServiceResponse<boolean>> {
    const visited = new Set<string>();
    const checkPath = async (currentId: string): Promise<boolean> => {
      if (currentId === predecessorId) {return true;}
      if (visited.has(currentId)) {return false;}

      visited.add(currentId);
      const { data: dependencies } = await this.supabase
        .from("task_dependencies")
        .select("predecessor_task_id")
        .eq("successor_task_id", currentId);

      if (dependencies) {
        for (const dep of dependencies) {
          if (await checkPath(dep.predecessor_task_id)) {
            return true;
          }
        }
      }
      return false;
    };

    const isCircular = await checkPath(successorId);
    return { success: true, data: isCircular };
  }
}
